import React, { useState, useEffect, createContext } from 'react';
import classNames from 'classnames';

const context = createContext({});

const { Provider, Consumer } = context;

const OpenModalButton = ({ id, children, className }) => {
  return (
    <Consumer>
      {({ changeModal, activeModalId }) => (
        <cite
          tabIndex="0"
          data-buttonid={`open${id}`}
          id={`button-${id}--opener`}
          className={classNames({
            [`${className}`]: true,
            [`${className}--open`]: activeModalId === id,
          })}
          aria-label="Referenser"
          onClick={() => changeModal(id)}
          onKeyDown={(e) => e.key === 'Enter' && changeModal(id)}
        >
          {children}
        </cite>
      )}
    </Consumer>
  );
};

const CloseModalButton = ({ children, id }) => (
  <Consumer>
    {({ closeModal }) => (
      <a
        autoFocus={id}
        tabIndex={0}
        className={'reference__modal-close'}
        onClick={() => closeModal(id)}
        onKeyDown={(e) => e.key === 'Enter' && closeModal()}
      >
        {children}
      </a>
    )}
  </Consumer>
);

const ModalPanel = ({ whenActive, children, hideWithCss }) => (
  <Consumer>
    {({ activeModalId }) =>
      activeModalId === whenActive || hideWithCss ? <>{children}</> : null
    }
  </Consumer>
);

const Modal = (props) => {
  if (typeof window === 'undefined') {
    return null;
  }

  const [activeModalId, setActiveModalId] = useState('');
  const tooltipTriggerElement = document.querySelector(
    '.reference__modal-open'
  );

  const handleTooltipPosition = () => {
    const screenPadding = 20;
    const tooltip = document.querySelector('.reference__modal-panel');
    const tooltipRect = tooltip.getBoundingClientRect();
    const tooltipRightX = tooltipRect.x + tooltipRect.width;
    const placeholderRightX =
      tooltipTriggerElement.x + tooltipTriggerElement.width;

    if (tooltipRect.x < 0) {
      tooltip.style.left = '-10px  ';
      tooltip.style.right = 'auto';
      tooltip.style.transform = `translateX(${
        -tooltipTriggerElement.x + screenPadding
      }px)`;
    } else if (tooltipRightX > window.outerWidth) {
      tooltip.style.left = 'auto';
      tooltip.style.right = '-10px';
      tooltip.style.transform = `translateX(${
        window.outerWidth - placeholderRightX - screenPadding
      }px)`;
    }
  };

  const changeModal = (newModalId) => {
    setActiveModalId(activeModalId === newModalId ? '' : newModalId);
  };

  const closeModal = (id) => {
    setActiveModalId('');

    //Give focus back to ref-opener
    if (document.querySelector(`[data-buttonid='open${id}']`)) {
      document.querySelector(`[data-buttonid='open${id}']`).focus();
    }
  };

  const escCloseHandler = (e) => {
    if (activeModalId !== '') {
      if (e.keyCode === 27) {
        e.preventDefault();
        closeModal();
      }
    }
    return null;
  };

  const handleClick = (e) => {
    //Close modal when clicking on anything that doesn't have this classname or is a link
    if (e.target.className.includes('reference__modal-element')) return null;
    if (e.target.tagName === 'A') return null;

    closeModal();
  };

  useEffect(() => {
    document.addEventListener('keydown', (e) => escCloseHandler(e));
    document.addEventListener('mousedown', (e) => handleClick(e));

    //Only handle position outside mobile breakpoint
    if (
      document.querySelector('.reference__modal-panel') &&
      window.outerWidth > 640
    ) {
      handleTooltipPosition();
    }
  }, [activeModalId]);

  useEffect(() => {
    return () => {
      document.removeEventListener('keydown', escCloseHandler());
      document.removeEventListener('mousedown', handleClick());
    };
  }, []);

  return (
    <Provider
      value={{
        activeModalId,
        changeModal,
        closeModal,
      }}
    >
      {props.children}
    </Provider>
  );
};

export default Modal;
export { OpenModalButton, CloseModalButton, ModalPanel, Consumer };
